import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";
import AddressMap from "@/components/AddressMap";
import { AddressFields } from "@/components/AddressFields";

export default function Register() {
  const { register, error } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [role, setRole] = useState("pool_owner");
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    municipality_name: "",
    municipality_id: "",
    requested_municipality_ids: [],
    address: "",
    lat: null,
    lng: null,
    pool_area: "",
    pool_depth: "",
  });
  const [addr, setAddr] = useState({ street: "", postal_code: "", city: "" });
  const [munis, setMunis] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/municipalities").then((r) => setMunis(r.data)).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleRequested = (id) => {
    setForm((f) => {
      const cur = new Set(f.requested_municipality_ids);
      if (cur.has(id)) cur.delete(id);
      else cur.add(id);
      return { ...f, requested_municipality_ids: [...cur] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      name: form.name,
      email: form.email,
      password: form.password,
      role,
      address: form.address || null,
      lat: form.lat,
      lng: form.lng,
    };
    if (role === "municipality") payload.municipality_name = form.municipality_name;
    else if (role === "pool_owner") {
      Object.assign(payload, addr);
      payload.pool_area = form.pool_area ? parseFloat(form.pool_area) : null;
      payload.pool_depth = form.pool_depth ? parseFloat(form.pool_depth) : null;
    } else if (role === "utility") {
      payload.requested_municipality_ids = form.requested_municipality_ids;
    }
    const ok = await register(payload);
    setLoading(false);
    if (ok) nav("/app");
  };

  return (
    <div className="mx-auto max-w-2xl py-10">
      <div className="label-caps mb-2">{t("create_account")}</div>
      <h1 className="mb-6 text-3xl font-bold tracking-tight">{t("register")}</h1>
      <Card className="border-border">
        <CardContent className="p-6">
          <form className="space-y-4" onSubmit={submit}>
            <div>
              <Label>{t("role")}</Label>
              <Select value={role} onValueChange={(v) => { setRole(v); setForm((f) => ({ ...f, address: "", lat: null, lng: null })); }}>
                <SelectTrigger data-testid="register-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pool_owner" data-testid="role-pool-owner">{t("pool_owner")}</SelectItem>
                  <SelectItem value="municipality" data-testid="role-municipality">{t("municipality")}</SelectItem>
                  <SelectItem value="utility" data-testid="role-utility">{t("utility")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>{t("name")}</Label>
                <Input required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="register-name-input" />
              </div>
              <div>
                <Label>{t("email")}</Label>
                <Input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="register-email-input" />
              </div>
              <div className="sm:col-span-2">
                <Label>{t("password")}</Label>
                <Input type="password" required minLength={6} value={form.password} onChange={(e) => update("password", e.target.value)} data-testid="register-password-input" />
              </div>
            </div>

            {role === "municipality" ? (
              <div>
                <Label>{t("municipality_name")}</Label>
                <Input required value={form.municipality_name} onChange={(e) => update("municipality_name", e.target.value)} data-testid="register-muni-name-input" />
              </div>
            ) : role === "pool_owner" ? (
              <>
                <AddressFields value={addr} onChange={setAddr} t={t} prefix="register" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label>{t("pool_area")}</Label>
                    <Input type="number" step="0.1" value={form.pool_area} onChange={(e) => update("pool_area", e.target.value)} data-testid="register-area-input" />
                  </div>
                  <div>
                    <Label>{t("pool_depth")}</Label>
                    <Input type="number" step="0.1" value={form.pool_depth} onChange={(e) => update("pool_depth", e.target.value)} data-testid="register-depth-input" />
                  </div>
                </div>
              </>
            ) : (
              <div data-testid="register-utility-scope">
                <Label>{t("utility_register_scope_label")}</Label>
                <p className="mb-2 text-xs text-muted-foreground">
                  {t("utility_register_scope_hint")}
                </p>
                <div className="max-h-56 overflow-y-auto rounded-md border border-border p-3">
                  {munis.length === 0 ? (
                    <div className="text-sm text-muted-foreground">—</div>
                  ) : (
                    munis.map((m) => {
                      const checked = form.requested_municipality_ids.includes(m.id);
                      return (
                        <label
                          key={m.id}
                          className="flex cursor-pointer items-center gap-2 py-1 text-sm"
                          data-testid={`register-utility-muni-${m.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRequested(m.id)}
                            className="h-4 w-4 accent-primary"
                          />
                          <span>{m.name}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            <div>
              <Label>{role === "pool_owner" ? t("map_location_optional") : t("address")}</Label>
              <AddressMap
                address={form.address}
                lat={form.lat}
                lng={form.lng}
                autoQuery={role === "pool_owner" && addr.street.trim() && /^\d{4,5}$/.test(addr.postal_code.trim()) && addr.city.trim() ? `${addr.street.trim()}, ${addr.postal_code.trim()} ${addr.city.trim()}` : ""}
                onChange={({ address, lat, lng }) => setForm((f) => ({ ...f, address, lat, lng }))}
              />
            </div>

            {error && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="register-error">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-btn">
              {loading ? "…" : t("register")}
            </Button>

            <div className="text-center text-sm text-muted-foreground">
              {t("have_account")}{" "}
              <Link to="/login" className="text-primary underline-offset-4 hover:underline" data-testid="go-login-link">
                {t("login")}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
